import _extends from "@babel/runtime/helpers/extends";
import React, { createContext, useContext } from 'react';
import { SHOW_DEPRECATION_WARNINGS } from '@bubble-dev/build-config';
export class DesignContext {
  getReferenceTokenId(tokenId, args) {
    var token = this.getDesignTokenValue(tokenId);
    var wasRef = false;
    var fromRef = (tokenId, args) => {
      wasRef = true;
      return {
        tokenId,
        args
      };
    };
    var value = token(args, fromRef);
    if (wasRef) {
      return value;
    }
    return null;
  }
  get(tokenId) {
    var args = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var token = this.getDesignTokenValue(tokenId);
    if (SHOW_DEPRECATION_WARNINGS) {
      if (token.tokenOptions && token.tokenOptions.deprecation && token.tokenOptions.deprecation._shown !== true) {
        var deprecation = token.tokenOptions.deprecation;
        var deprecationWarning = "BUBBLE-UI DEPRECATION WARNING: '".concat(tokenId, "' token is deprecated and will be removed");
        if (deprecation.removalDate) {
          deprecationWarning += " in/by ".concat(deprecation.removalDate);
        }
        deprecationWarning += ".";
        if (deprecation.useInstead) {
          deprecationWarning += "\nUse '".concat(deprecation.useInstead, "' instead.");
        }
        if (deprecation.readMore) {
          deprecationWarning += "\nRead more on ".concat(deprecation.readMore);
        }
        token.tokenOptions.deprecation._shown = true;
        console.warn(deprecationWarning);
      }
    }
    var self = this;
    var fromRef = (t, a) => {
      if (SHOW_DEPRECATION_WARNINGS) {
        var refToken = self.getDesignTokenValue(t);
        var showDeprecation = refToken.tokenOptions && refToken.tokenOptions.deprecation && refToken.tokenOptions.deprecation._shown !== true;
        if (showDeprecation) {
          console.warn("designtoken ".concat(tokenId, " is referring to deprecated token ").concat(t));
        }
      }
      return self.get(t, a);
    };
    return token(args, fromRef);
  }
  bindDefaultTokens(defaultTokens, overrideTokens) {
    if (!defaultTokens && !overrideTokens) {
      return this;
    }
    return new DesignContextProxy(this, defaultTokens, overrideTokens);
  }
}
export class DesignContextProxy extends DesignContext {
  constructor(designContext, defaultTokens, overrideTokens) {
    super();
    this.designContext = designContext;
    this.defaultTokens = defaultTokens;
    this.overrideTokens = overrideTokens;
  }
  hasTokenInContext(tokenId) {
    var _this$overrideTokens, _this$defaultTokens;
    return Boolean(this === null || this === void 0 ? void 0 : (_this$overrideTokens = this.overrideTokens) === null || _this$overrideTokens === void 0 ? void 0 : _this$overrideTokens[tokenId]) || this.designContext.hasTokenInContext(tokenId) || Boolean(this === null || this === void 0 ? void 0 : (_this$defaultTokens = this.defaultTokens) === null || _this$defaultTokens === void 0 ? void 0 : _this$defaultTokens[tokenId]);
  }
  getDesignTokenValue(tokenId) {
    var _this$overrideTokens2, _this$defaultTokens2;
    if (this !== null && this !== void 0 && (_this$overrideTokens2 = this.overrideTokens) !== null && _this$overrideTokens2 !== void 0 && _this$overrideTokens2[tokenId]) {
      var _this$overrideTokens3;
      return this === null || this === void 0 ? void 0 : (_this$overrideTokens3 = this.overrideTokens) === null || _this$overrideTokens3 === void 0 ? void 0 : _this$overrideTokens3[tokenId];
    }
    if (this.designContext.hasTokenInContext(tokenId)) {
      return this.designContext.getDesignTokenValue(tokenId);
    }
    if (this !== null && this !== void 0 && (_this$defaultTokens2 = this.defaultTokens) !== null && _this$defaultTokens2 !== void 0 && _this$defaultTokens2[tokenId]) {
      var _this$defaultTokens3;
      return this === null || this === void 0 ? void 0 : (_this$defaultTokens3 = this.defaultTokens) === null || _this$defaultTokens3 === void 0 ? void 0 : _this$defaultTokens3[tokenId];
    }
    throw "cannot find token ".concat(tokenId);
  }
}
export function useDesignToken(ctx, tokenId) {
  var args = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return React.useMemo(() => ctx.get(tokenId, args), [ctx, tokenId, args]);
}
export class DesignContextTokenStore extends DesignContext {
  constructor(tokens) {
    super();
    this.tokens = tokens;
  }
  hasTokenInContext(tokenId) {
    return this.tokens[tokenId] !== undefined;
  }
  getDesignTokenValue(tokenId) {
    var token;
    if (this.tokens[tokenId] !== undefined) {
      token = this.tokens[tokenId];
    }
    if (!token) {
      // @TODO: really throw error?
      throw "cannot find token ".concat(tokenId);
    }
    return token;
  }
}
var DesignContextContext = /*#__PURE__*/createContext(new DesignContextTokenStore({}));
export var DesignContextProvider = _ref => {
  var {
    tokens,
    children
  } = _ref;
  var designContextStore = new DesignContextTokenStore(tokens);
  return /*#__PURE__*/React.createElement(DesignContextContext.Provider, {
    value: designContextStore
  }, children);
};
export var DesignContextForwardProvider = _ref2 => {
  var {
    context,
    children
  } = _ref2;
  return /*#__PURE__*/React.createElement(DesignContextContext.Provider, {
    value: context
  }, children);
};
export var DesignContextDefaultsProvider = _ref3 => {
  var {
    tokens: tokenOverrides,
    children
  } = _ref3;
  var designContext = useDesignContext(tokenOverrides);
  return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, children);
};
export var DesignContextOverridesProvider = _ref4 => {
  var {
    tokens,
    children
  } = _ref4;
  var designContext = useDesignContext(undefined, tokens);
  return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, children);
};
export var DesignContextConsumer = DesignContextContext.Consumer;
export var useDesignContext = (defaultTokens, overrideTokens) => {
  var context = useContext(DesignContextContext);
  return React.useMemo(() => context.bindDefaultTokens(defaultTokens, overrideTokens), [defaultTokens, overrideTokens, context]);
};
export var withDesignContext = defaultTokens => Target => props => {
  return /*#__PURE__*/React.createElement(DesignContextContext.Consumer, null, designContext => /*#__PURE__*/React.createElement(Target, _extends({}, props, {
    designContext: designContext.bindDefaultTokens(defaultTokens)
  })));
};
//# sourceMappingURL=DesignContext.js.map